import React, { createContext, useContext, useState, ReactNode } from 'react'
import {
  ClientResponse,
  TerminalResponse,
  CertificateResponse,
} from '../../types'
import { trpc } from '../utils'

// Types
export interface CertificateSelection {
  cert: CertificateResponse
  terminal: TerminalResponse
  client: ClientResponse
  pinFromClient?: boolean
}

interface CertificateContextType {
  selectedCertificate: CertificateSelection | null
  handleSelectedCertificate: (cert: CertificateSelection | null) => void
  allCertificates: Array<{
    cert: CertificateResponse
    terminal: TerminalResponse
    client: ClientResponse
    displayName: string
  }>
  loadAllCertificates: () => Promise<void>
  certificateLoading: boolean
  certificateError: string | null
}

const CertificateContext = createContext<CertificateContextType | undefined>(
  undefined,
)

export const CertificateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedCertificate, setSelectedCertificate] =
    useState<CertificateSelection | null>(null)
  const [allCertificates, setAllCertificates] = useState<
    Array<{
      cert: CertificateResponse
      terminal: TerminalResponse
      client: ClientResponse
      displayName: string
    }>
  >([])
  const [certificateLoading, setCertificateLoading] = useState(false)
  const [certificateError, setCertificateError] = useState<string | null>(null)

  const loadAllCertificates = async () => {
    setCertificateLoading(true)
    setCertificateError(null)

    try {
      const result = await trpc.getAllCertificates.query()

      const certificates = result.certificates
      setAllCertificates(certificates)

      if (
        Array.isArray(certificates) &&
        certificates.length > 0 &&
        certificates[0].cert?.serialNumber
      ) {
        const first = certificates[0]
        setSelectedCertificate({
          cert: first.cert,
          terminal: first.terminal,
          client: first.client,
          pinFromClient: first.cert.pinFromClient,
        })
      }
    } catch (err) {
      console.error('Veri yüklenirken hata:', err)
      setCertificateError('Veriler yüklenirken bir hata oluştu')
    } finally {
      setCertificateLoading(false)
    }
  }

  const handleSelectedCertificate = (cert: CertificateSelection | null) => {
    if (!cert) {
      setSelectedCertificate(null)
      return
    }

    setSelectedCertificate({
      ...cert,
      pinFromClient: cert.pinFromClient,
    })
  }

  const contextValue: CertificateContextType = {
    selectedCertificate,
    handleSelectedCertificate,
    allCertificates,
    loadAllCertificates,
    certificateLoading,
    certificateError,
  }

  return (
    <CertificateContext.Provider value={contextValue}>
      {children}
    </CertificateContext.Provider>
  )
}

export const useCertificateContext = () => {
  const context = useContext(CertificateContext)
  if (context === undefined) {
    throw new Error(
      'useCertificateContext must be used within a CertificateProvider',
    )
  }
  return context
}
